import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Button,
  SelectChangeEvent,
  OutlinedInput,
  Chip,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  Schedule as ScheduleIcon,
  DateRange as DateRangeIcon,
  TableRestaurant as TableRestaurantIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import MonthlyCalendar from './MonthlyCalendar';
import DailyTimeline from './DailyTimeline';
import WeeklyCalendar from './WeeklyCalendar';
import TableReservationDialog from './TableReservationDialog';
import PrivateLessonRequestDialog from './PrivateLessonRequestDialog';
import { getTodayLocal } from '../../utils/dateUtils';
import { venueService } from '../../services/venueService';
import { Venue } from '../../types/venue';
import { useAuth } from '../../contexts/AuthContext';
import eventService, { EventTypeDto } from '../../services/eventService';
import { userService } from '../../services/userService';
import { UserListItem } from '../../types/user';

type CalendarView = 'monthly' | 'weekly' | 'daily';

interface CalendarProps {
  onNavigateToUserProfile?: (userId: string) => void;
  /** Az esemény ablakából a jelenléti ívre visz, ARRA az eseményre. */
  onNavigateToAttendance?: (eventId: string, eventDate: string) => void;
}

const Calendar: React.FC<CalendarProps> = ({ onNavigateToUserProfile, onNavigateToAttendance }) => {
  const { user } = useAuth();
  const [view, setView] = useState<CalendarView>('weekly');
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return getTodayLocal();
  });

  // Filter state
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  // „Betelt edzések elrejtése (a sajátjaim kivételével)" — a JÁTÉKOSNÁL alapból BE.
  //
  // A naptár tele volt idegen, betelt órákkal (leginkább mások magánóráival),
  // amikre a játékos úgysem tud jelentkezni. Kikapcsolható: nem eltitkoljuk,
  // csak alapból nem mutatjuk. Adminnál/edzőnél KI — nekik a teljes kép kell.
  //
  // Ez az EGYETLEN ilyen szűrő. Volt mellette egy „Csak szabad helyekkel
  // rendelkező események" jelölőnégyzet is, de a kettő majdnem ugyanazt csinálta,
  // és egymás mellett zavaró volt. A különbség annyi, hogy ez a SAJÁT eseményeidet
  // akkor is mutatja, ha beteltek — azokat senki nem akarja elrejteni.
  const [onlyRelevant, setOnlyRelevant] = useState<boolean>(
    () => user?.userRole !== 'Administrator' && user?.userRole !== 'Coach',
  );
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loadingVenues, setLoadingVenues] = useState<boolean>(true);

  // New filter states
  const [selectedEventTypeIds, setSelectedEventTypeIds] = useState<string[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeDto[]>([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState<boolean>(true);

  const [selectedCoachIds, setSelectedCoachIds] = useState<string[]>([]);
  const [coaches, setCoaches] = useState<UserListItem[]>([]);
  const [loadingCoaches, setLoadingCoaches] = useState<boolean>(true);

  // Table Reservation Dialog state
  const [reservationDialogOpen, setReservationDialogOpen] = useState<boolean>(false);
  const [privateLessonDialogOpen, setPrivateLessonDialogOpen] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Load venues, event types, and coaches on component mount
  useEffect(() => {
    const loadVenues = async () => {
      try {
        setLoadingVenues(true);
        const venuesData = await venueService.getActiveVenues();
        setVenues(venuesData);
      } catch (error) {
        console.error('Error loading venues:', error);
      } finally {
        setLoadingVenues(false);
      }
    };

    const loadEventTypes = async () => {
      try {
        setLoadingEventTypes(true);
        const eventTypesData = await eventService.getEventTypes();
        setEventTypes(eventTypesData);
      } catch (error) {
        console.error('Error loading event types:', error);
      } finally {
        setLoadingEventTypes(false);
      }
    };

    const loadCoaches = async () => {
      try {
        setLoadingCoaches(true);
        const coachesData = await userService.getCoaches();
        setCoaches(coachesData);
      } catch (error) {
        console.error('Error loading coaches:', error);
      } finally {
        setLoadingCoaches(false);
      }
    };

    loadVenues();
    loadEventTypes();
    loadCoaches();
  }, []);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: CalendarView | null) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleVenueFilterChange = (event: any) => {
    setSelectedVenueId(event.target.value);
  };

  const handleOnlyRelevantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOnlyRelevant(event.target.checked);
  };

  const handleEventTypeFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedEventTypeIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleCoachFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedCoachIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    if (view === 'monthly' || view === 'weekly') {
      setView('daily');
    }
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleReservationSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Check if user is not Administrator (Player or Coach can make reservations)
  const canMakeReservation = user?.userRole !== 'Administrator';
  // Private-lesson requests are a player-only action (coaches/admins arrange lessons directly).
  const isPlayer = user?.userRole !== 'Administrator' && user?.userRole !== 'Coach';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 2 },
        mb: 3
      }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Esemény naptár
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          {canMakeReservation && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<TableRestaurantIcon />}
              onClick={() => setReservationDialogOpen(true)}
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 }
              }}
            >
              Asztalfoglalás
            </Button>
          )}

          {isPlayer && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SchoolIcon />}
              onClick={() => setPrivateLessonDialogOpen(true)}
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 }
              }}
            >
              Magánóra igénylése
            </Button>
          )}

          <Paper sx={{ p: 0.5, alignSelf: { xs: 'center', sm: 'auto' } }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              aria-label="calendar view"
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  px: { xs: 0.75, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  minWidth: { xs: 'auto', sm: 'unset' },
                  flexShrink: 1
                }
              }}
            >
              <ToggleButton value="monthly" aria-label="monthly view">
                <CalendarMonthIcon sx={{ mr: { xs: 0.25, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Havi nézet</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Havi</Box>
              </ToggleButton>
              <ToggleButton value="weekly" aria-label="weekly view">
                <DateRangeIcon sx={{ mr: { xs: 0.25, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Heti nézet</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Heti</Box>
              </ToggleButton>
              <ToggleButton value="daily" aria-label="daily view">
                <ScheduleIcon sx={{ mr: { xs: 0.25, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Napi nézet</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Napi</Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {/* First row - Event Type and Coach filters */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', md: 'center' }
          }}>
            <FormControl sx={{ minWidth: { xs: '100%', md: 250 } }} size="small">
              <InputLabel id="event-type-filter-label">Esemény típus</InputLabel>
              <Select
                labelId="event-type-filter-label"
                multiple
                value={selectedEventTypeIds}
                onChange={handleEventTypeFilterChange}
                input={<OutlinedInput label="Esemény típus" />}
                disabled={loadingEventTypes}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Minden esemény</em>;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const eventType = eventTypes.find((et) => et.eventTypeId === value);
                        return (
                          <Chip
                            key={value}
                            label={eventType?.displayName || value}
                            size="small"
                            sx={{
                              backgroundColor: eventType?.color || 'grey.400',
                              color: 'white',
                              height: '20px',
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {eventTypes.map((eventType) => (
                  <MenuItem key={eventType.eventTypeId} value={eventType.eventTypeId}>
                    <Checkbox checked={selectedEventTypeIds.indexOf(eventType.eventTypeId) > -1} />
                    <Chip
                      label={eventType.displayName}
                      size="small"
                      sx={{
                        backgroundColor: eventType.color,
                        color: 'white',
                        ml: 1,
                        height: '24px'
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: { xs: '100%', md: 250 } }} size="small">
              <InputLabel id="coach-filter-label">Edző</InputLabel>
              <Select
                labelId="coach-filter-label"
                multiple
                value={selectedCoachIds}
                onChange={handleCoachFilterChange}
                input={<OutlinedInput label="Edző" />}
                disabled={loadingCoaches}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Összes edző</em>;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const coach = coaches.find((c) => c.userId === value);
                        return (
                          <Chip
                            key={value}
                            label={coach?.name || value}
                            size="small"
                            sx={{ height: '20px' }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {coaches.map((coach) => (
                  <MenuItem key={coach.userId} value={coach.userId}>
                    <Checkbox checked={selectedCoachIds.indexOf(coach.userId) > -1} />
                    {coach.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Second row - Venue and Available Only filters */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', md: 'center' }
          }}>
            <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }} size="small">
              <InputLabel id="venue-filter-label">Helyszín szűrő</InputLabel>
              <Select
                labelId="venue-filter-label"
                value={selectedVenueId}
                label="Helyszín szűrő"
                onChange={handleVenueFilterChange}
                disabled={loadingVenues}
              >
                <MenuItem value="">
                  <em>Minden helyszín</em>
                </MenuItem>
                {venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyRelevant}
                  onChange={handleOnlyRelevantChange}
                  name="onlyRelevant"
                  color="primary"
                  inputProps={{ 'aria-label': 'Betelt edzések elrejtése (a sajátjaim kivételével)' }}
                />
              }
              label="Betelt edzések elrejtése (a sajátjaim kivételével)"
              sx={{
                ml: { md: 2 },
                alignSelf: { xs: 'flex-start', md: 'center' },
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            />
          </Box>
        </Box>
      </Paper>

      {view === 'monthly' ? (
        <MonthlyCalendar
          onDateSelect={handleDateSelect}
          selectedDate={selectedDate}
          venueFilter={selectedVenueId}
          excludeFullOthers={onlyRelevant}
          eventTypeFilter={selectedEventTypeIds}
          coachFilter={selectedCoachIds}
          refreshTrigger={refreshTrigger}
          onNavigateToUserProfile={onNavigateToUserProfile}
          onNavigateToAttendance={onNavigateToAttendance}
        />
      ) : view === 'weekly' ? (
        <WeeklyCalendar
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          onDateSelect={handleDateSelect}
          venueFilter={selectedVenueId}
          excludeFullOthers={onlyRelevant}
          eventTypeFilter={selectedEventTypeIds}
          coachFilter={selectedCoachIds}
          refreshTrigger={refreshTrigger}
          onNavigateToUserProfile={onNavigateToUserProfile}
          onNavigateToAttendance={onNavigateToAttendance}
        />
      ) : (
        <DailyTimeline
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          venueFilter={selectedVenueId}
          excludeFullOthers={onlyRelevant}
          eventTypeFilter={selectedEventTypeIds}
          coachFilter={selectedCoachIds}
          refreshTrigger={refreshTrigger}
          onNavigateToUserProfile={onNavigateToUserProfile}
          onNavigateToAttendance={onNavigateToAttendance}
        />
      )}

      <TableReservationDialog
        open={reservationDialogOpen}
        onClose={() => setReservationDialogOpen(false)}
        onSuccess={handleReservationSuccess}
        initialDate={selectedDate}
      />

      <PrivateLessonRequestDialog
        open={privateLessonDialogOpen}
        onClose={() => setPrivateLessonDialogOpen(false)}
      />
    </Box>
  );
};

export default Calendar;